import { Controller, useForm, useFormState } from "react-hook-form";
import Button, { ButtonProps } from "@vfuk/core-button";
import { FloatingLabelInput, Stack, useDebounce } from "@ucchub/ui";
import Container from "@vfuk/core-container";
import { Grid, GridRow, GridColumn } from "@vfuk/core-grid";
import Spacing from "@vfuk/core-spacing";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import CustomerFormHeader from "./CustomerFormHeader";
import CustomerDiscardPopup from "./CustomerDiscardPopup";
import { Customer, CustomerFormProps } from "@interfaces/customer";
import ConfirmModalContent from "./CustomerConfirmPopup";
import useCustomerDetails from "@hooks/Customers/useCustomerCreateEdit";
import { getCustomerRules } from "./CustomerRules";
import { t } from "i18next";
import CustomerFormPid from "./CustomerFormPid";
import { CustomerDetails } from "@components/customer/Customer.types";

const CustomerForm = ({ isDraft, customerId, customer }: CustomerFormProps) => {
  const formHandler = useForm({ mode: "onTouched", shouldFocusError: false });
  const { control } = formHandler;
  const { isCustomerNameExist, addCustomer, updateCustomer, isPIDNameExist } =
    useCustomerDetails();
  const { handleSubmit, formState, getValues, reset } = formHandler;
  const { dirtyFields } = useFormState({ control });
  const { errors, isValid, isDirty } = formState;
  const navigate = useNavigate();
  const [disableBtn, setDisableBtn] =
    useState<ButtonProps["state"]>("disabled");
  const [submittedDraft, setSubmittedDraft] = useState(false);
  const [showDiscardModal, setShowDiscardModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [customerData, setCustomerData] = useState<Customer>({});
  const [previousCustomerData, setPreviousCustomerData] =
    useState<CustomerDetails>();
  const [searchCustomer, setSearchCustomer] = useState("");
  const [searchPID, setSearchPID] = useState("");
  const [isNameExist, setNameExist] = useState(false);
  const [isPidExist, setPidExist] = useState(false);
  const [isPidLoading, setPidLoading] = useState(false);
  const [name, addressLine1, country, city, postcode, contactEmail, pid] =
    getValues([
      "name",
      "addressLine1",
      "country",
      "city",
      "postcode",
      "contactEmail",
      "pid",
    ]);
  const customerSearch = useDebounce<string>(searchCustomer, 500);
  const pidSearch = useDebounce<string>(searchPID, 500);
  const customerRules = getCustomerRules();
  useEffect(() => {
    if (customerSearch && customerSearch !== customer?.name) {
      isCustomerNameExist(customerSearch).then((isNameExist: boolean) => {
        setNameExist(isNameExist);
      });
    } else {
      setNameExist(false);
    }
    if (
      pidSearch &&
      pidSearch !== customer?.pid &&
      pidSearch.length > 5 &&
      pidSearch.length < 8
    ) {
      setPidLoading(true);
      isPIDNameExist(pidSearch).then((isPIDExist: boolean) => {
        setPidExist(isPIDExist);
        setPidLoading(false);
      });
    } else {
      setPidExist(false);
      setPidLoading(false);
    }
  }, [customerSearch, pidSearch]);
  const activateDraftButton = (): boolean => {
    let activate = name && !isNameExist;
    Object.keys(errors).map((index) => {
      activate = errors[index]?.type !== "required" ? false : activate;
    });
    activate =
      activate &&
      (addressLine1 || country || city || postcode || contactEmail || pid);
    activate = pid && isPidExist ? false : activate;
    activate =
      JSON.stringify(formState.defaultValues) === JSON.stringify(getValues())
        ? false
        : activate;
    return activate;
  };
  const draftConfirmButton =
    name && addressLine1 && country && city && postcode && contactEmail && pid;

  const customer_id = previousCustomerData?.id || customerId;
  const draft = isDraft || submittedDraft;
  const customer_data = previousCustomerData || customer;
  const customerPIDText = !errors?.pid && !isPidExist;
  const onSubmit = handleSubmit((data) => {
    setCustomerData(data);
    setShowConfirmModal(true);
  });
  const onSubmitAsDraft = () => {
    const getDetails = getValues();
    if (isDraft || submittedDraft) {
      updateCustomer(getDetails, customer_id, false, true, customer_data).then(
        (response) => {
          if (response.status === "success") {
            setSubmittedDraft(true);
            reset(getDetails);
          } else if (response.status === "error") {
            setShowConfirmModal(false);
          }
        }
      );
    } else {
      addCustomer(getDetails, true, false).then((response) => {
        if (response.status === "success") {
          setPreviousCustomerData(response?.customerData);
          setSubmittedDraft(true);
          reset(getDetails);
        }
      });
    }
  };
  useEffect(() => {
    if (isDraft && draftConfirmButton && !isPidLoading) {
      setDisableBtn(undefined);
    }
    if (isDirty) {
      if (isValid && !isNameExist && !isPidExist && !isPidLoading) {
        setDisableBtn(undefined);
      } else {
        setDisableBtn("disabled");
      }
    }
  }, [isDirty, isValid, formState]);
  useEffect(() => {
    if (isDraft) {
      reset(customer);
    }
  }, [isDraft]);
  return (
    <>
      {showConfirmModal && (
        <ConfirmModalContent
          data={customerData}
          isDraft={draft}
          customerId={customer_id}
          oldData={customer_data}
          showModal={() => setShowConfirmModal(false)}
        />
      )}
      {showDiscardModal && (
        <CustomerDiscardPopup
          isOpen={showDiscardModal}
          onCancel={() => {
            setShowDiscardModal(false);
          }}
          onConfirm={() => {
            navigate(`/customers/`);
          }}
        />
      )}
      <Container appearance="secondary">
        <form onSubmit={onSubmit}>
          <Spacing spacingLevel={{ top: 4 }}>
            <Container elevation>
              <CustomerFormHeader />
              <Grid>
                <GridRow marginTop={3} marginBottom={3}>
                  <GridColumn col={12} colMd={4} colLg={4}>
                    <Controller
                      name="name"
                      control={control}
                      rules={customerRules.draftCustomerName}
                      render={({ field }) => (
                        <FloatingLabelInput
                          label={t("core:customers:label:customer_name")}
                          error={
                            isNameExist
                              ? t("core:customers:customer_exists") || undefined
                              : errors.name
                              ? (errors.name?.message as string) || undefined
                              : undefined
                          }
                          type="text"
                          id="name"
                          state={
                            errors.name || isNameExist ? "error" : undefined
                          }
                          {...field}
                          onChange={(e) => {
                            setSearchCustomer(e.target.value.trim());
                            field.onChange(e.target.value);
                          }}
                        />
                      )}
                    />
                  </GridColumn>
                </GridRow>
                <GridRow marginTop={3} marginBottom={3}>
                  <GridColumn col={12} colMd={4} colLg={4}>
                    <Controller
                      name="addressLine1"
                      control={control}
                      rules={customerRules.addressLine1}
                      render={({ field }) => (
                        <FloatingLabelInput
                          label={t("core:customers:label:address_line_1")}
                          error={
                            errors.addressLine1
                              ? (errors.addressLine1.message as string)
                              : undefined
                          }
                          type="text"
                          id="addressLine1"
                          state={errors.addressLine1 ? "error" : undefined}
                          {...field}
                        />
                      )}
                    />
                  </GridColumn>
                  <GridColumn col={12} colMd={4} colLg={4}>
                    <Controller
                      name="addressLine2"
                      control={control}
                      rules={customerRules.addressLine2}
                      render={({ field }) => (
                        <FloatingLabelInput
                          label={t("core:customers:label:address_line_2")}
                          error={
                            errors.addressLine2
                              ? (errors.addressLine2.message as string)
                              : undefined
                          }
                          type="text"
                          id="addressLine2"
                          {...field}
                          state={errors.addressLine2 ? "error" : undefined}
                        />
                      )}
                    />
                  </GridColumn>
                </GridRow>
                <GridRow marginTop={3} marginBottom={3}>
                  <GridColumn col={12} colMd={4} colLg={4}>
                    <Controller
                      name="country"
                      control={control}
                      rules={customerRules.country}
                      render={({ field }) => (
                        <FloatingLabelInput
                          label={t("core:customers:label:country")}
                          type="text"
                          id="country"
                          error={
                            errors.country
                              ? (errors.country.message as string)
                              : undefined
                          }
                          {...field}
                          state={errors.country ? "error" : undefined}
                        />
                      )}
                    />
                  </GridColumn>
                  <GridColumn col={12} colMd={4} colLg={4}>
                    <Controller
                      name="city"
                      control={control}
                      rules={customerRules.city}
                      render={({ field }) => (
                        <FloatingLabelInput
                          label={t("core:customers:label:city")}
                          error={
                            errors.city
                              ? (errors.city.message as string)
                              : undefined
                          }
                          type="text"
                          id="city"
                          {...field}
                          state={errors.city ? "error" : undefined}
                        />
                      )}
                    />
                  </GridColumn>
                  <GridColumn col={12} colMd={4} colLg={4}>
                    <Controller
                      name="area"
                      control={control}
                      rules={customerRules.area}
                      render={({ field }) => (
                        <FloatingLabelInput
                          label={t("core:customers:label:area")}
                          error={
                            errors.area
                              ? (errors.area.message as string)
                              : undefined
                          }
                          type="text"
                          id="area"
                          {...field}
                          state={errors.area ? "error" : undefined}
                        />
                      )}
                    />
                  </GridColumn>
                </GridRow>
                <GridRow marginTop={3} marginBottom={3}>
                  <GridColumn col={12} colMd={4} colLg={4}>
                    <Controller
                      name="postcode"
                      control={control}
                      rules={customerRules.postcode}
                      render={({ field }) => (
                        <FloatingLabelInput
                          label={t("core:customers:label:post_code")}
                          error={
                            errors.postcode
                              ? (errors.postcode.message as string)
                              : undefined
                          }
                          type="text"
                          id="postcode"
                          {...field}
                          state={errors.postcode ? "error" : undefined}
                        />
                      )}
                    />
                  </GridColumn>
                  <GridColumn col={12} colMd={4} colLg={4}>
                    <Controller
                      name="phoneNumber"
                      control={control}
                      rules={customerRules.phoneNumber}
                      render={({ field }) => (
                        <FloatingLabelInput
                          label={t("core:customers:label:phone_number")}
                          error={
                            errors.phoneNumber
                              ? (errors.phoneNumber.message as string)
                              : undefined
                          }
                          type="text"
                          id="phoneNumber"
                          {...field}
                          state={errors.phoneNumber ? "error" : undefined}
                        />
                      )}
                    />
                  </GridColumn>
                </GridRow>
                <GridRow marginTop={3} marginBottom={3}>
                  <GridColumn col={12} colMd={4} colLg={4}>
                    <Controller
                      name="contactFirstName"
                      control={control}
                      rules={customerRules.firstName}
                      render={({ field }) => (
                        <FloatingLabelInput
                          label={t("core:customers:label:contact_first_name")}
                          error={
                            errors.contactFirstName
                              ? (errors.contactFirstName.message as string)
                              : undefined
                          }
                          type="text"
                          id="contactFirstName"
                          {...field}
                          state={errors.contactFirstName ? "error" : undefined}
                        />
                      )}
                    />
                  </GridColumn>
                  <GridColumn col={12} colMd={4} colLg={4}>
                    <Controller
                      name="contactLastName"
                      control={control}
                      rules={customerRules.lastName}
                      render={({ field }) => (
                        <FloatingLabelInput
                          label={t("core:customers:label:contact_last_name")}
                          error={
                            errors.contactLastName
                              ? (errors.contactLastName.message as string)
                              : undefined
                          }
                          type="text"
                          id="contactLastName"
                          {...field}
                          state={errors.contactLastName ? "error" : undefined}
                        />
                      )}
                    />
                  </GridColumn>
                  <GridColumn col={12} colMd={4} colLg={4}>
                    <Controller
                      name="contactEmail"
                      control={control}
                      rules={customerRules.email}
                      render={({ field }) => (
                        <FloatingLabelInput
                          label={t("core:customers:label:contact_email")}
                          error={
                            errors.contactEmail
                              ? (errors.contactEmail.message as string)
                              : undefined
                          }
                          type="text"
                          id="contactEmail"
                          {...field}
                          state={errors.contactEmail ? "error" : undefined}
                        />
                      )}
                    />
                  </GridColumn>
                </GridRow>
                <GridRow marginTop={3} marginBottom={3}>
                  <GridColumn col={12} colMd={4} colLg={4}>
                    <Controller
                      name="pid"
                      control={control}
                      rules={customerRules.pid}
                      render={({ field }) => (
                        <FloatingLabelInput
                          label={t("core:customers:label:pid")}
                          error={
                            isPidExist
                              ? t("core:customers:pid_exists") || undefined
                              : errors.pid
                              ? (errors.pid?.message as string) || undefined
                              : undefined
                          }
                          type="text"
                          id="pid"
                          state={errors.pid || isPidExist ? "error" : undefined}
                          {...field}
                          onChange={(e) => {
                            setSearchPID(e.target.value.trim());
                            field.onChange(e.target.value);
                          }}
                        />
                      )}
                    />
                    {customerPIDText && <CustomerFormPid />}
                  </GridColumn>
                </GridRow>
              </Grid>
            </Container>
          </Spacing>
          <Spacing spacingLevel={{ top: 4 }}>
            <Stack justifyContent="space-between">
              <Spacing spacingLevel={{ right: 2 }}>
                <Button
                  appearance="alt1"
                  text={t("core:customers:cancel")}
                  onClick={() => {
                    if (
                      disableBtn === "disabled" &&
                      Object.keys(dirtyFields).length === 0
                    ) {
                      navigate("/customers");
                    } else {
                      setShowDiscardModal(true);
                    }
                  }}
                />
              </Spacing>
              <Stack>
                <Spacing spacingLevel={{ right: 2 }}>
                  <Button
                    text={t("core:customers:save_as_draft")}
                    onClick={() => {
                      onSubmitAsDraft();
                    }}
                    appearance="secondary"
                    state={
                      activateDraftButton() && isDirty ? undefined : "disabled"
                    }
                  />
                </Spacing>
                <Button
                  onClick={onSubmit} // Empty `onClick` adds the 'pointer' cursor
                  text={t("core:customers:confirm")}
                  appearance={disableBtn ? "secondary" : "primary"}
                  state={disableBtn || undefined}
                />
              </Stack>
            </Stack>
          </Spacing>
        </form>
      </Container>
    </>
  );
};
export default CustomerForm;
